import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import SearchFilterViewModel from './ViewModels/SearchFilterViewmodel';
import Chip from '@mui/material/Chip';
import { useContext, useState, useEffect, Fragment } from 'react';
import { camelCaseToTitleCase } from '../../Utilities/StringUtils';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { OrganisationsContext } from '../../Contexts/OrganisationsContext';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

interface IProps {
    open: boolean;
    onClose: (refresh: boolean) => void;
    dropdowns: Record<string, DropDownViewModel[]>;
}

export default function SearchFiltersDialog(props: IProps) {
    const { open, onClose, dropdowns } = props;
    const { filterState, updateFilterState } = useContext(OrganisationsContext);
    const [currentFilters, setCurrentFilters] = useState<SearchFilterViewModel>({ ...filterState });

    useEffect(() => {
        if (open)
            setCurrentFilters({ ...filterState });
    }, [open]);

    const dropDownChange = (name: string, value: any) => {
        setCurrentFilters((prev) => ({
            ...prev,
            [name]: name === 'expiryDate' ? { ...prev.expiryDate, ...value } : value,
            pageNumber: 0,
        }));
    };

    const handleChipClick = (filterKey: keyof SearchFilterViewModel) => {
        setCurrentFilters((prev) => ({
            ...prev,
            [filterKey]: prev[filterKey] ?
                null
                :
                filterKey === "expiryDate" ?
                    new DateRange()
                    :
                    filterKey === 'active' /*|| filterKey === 'administratorSitesOnly' */ ?
                        true
                        :
                        " ",
            pageNumber: 0,
        }));
    };

    const handleSave = () => {
        updateFilterState(currentFilters, () =>
            onClose(true)
        );
    };

    const renderFilterComponent = (key: string, disable: boolean) => {
        if (key === 'expiryDate') {
            const { start, end } = currentFilters.expiryDate || {};
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        value={start ? dayjs(start) : null}
                        onChange={(value) => dropDownChange(key, { start: value?.toDate() || null })}
                        slotProps={{ textField: { size: 'small' } }}
                        disabled={disable}
                    />
                    <DatePicker
                        value={end ? dayjs(end) : null}
                        onChange={(value) => dropDownChange(key, { end: value?.toDate() || null })}
                        slotProps={{ textField: { size: 'small' } }}
                        disabled={disable}
                    />
                </LocalizationProvider>
            );
        }

        if (key === 'active' || key === 'administratorSitesOnly') {
            return <></>;
        }

        return dropdowns[key] ? (
            <TextField
                sx={{ minWidth: 250 }}
                size="small"
                fullWidth
                select
                value={currentFilters[key]}
                onChange={(e) => dropDownChange(key, e.target.value)}
                disabled={disable}
            >
                {(dropdowns[key] || []).map((item, index) =>
                    <MenuItem key={key + index} value={item.id}>{item.label}</MenuItem>
                )}
            </TextField>
        ) : (
            <TextField
                fullWidth
                size="small"
                value={currentFilters[key]}
                onChange={(e) => dropDownChange(key, e.target.value)}
                disabled={disable}
            />
        );
    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>Select Filters</DialogTitle>
            <DialogContent>
                <Grid container justifyContent="start">
                    {Object.keys(currentFilters)
                        .filter(f => f !== 'pageNumber' && f !== 'rowsPerPage' && f !== 'order' && f !== 'orderBy' && f !== 'orderByProp' && f !== 'quickSearch').map((key) => (
                            <Fragment key={key}>
                                <Grid size={(key === 'active' || key === 'administratorSitesOnly') ? 'auto' : 2}>
                                    <Chip
                                        sx={{ m: '4px' }}
                                        label={camelCaseToTitleCase(key)}
                                        clickable
                                        color={currentFilters[key] ? 'primary' : 'default'}
                                        onClick={(e) => handleChipClick(key as keyof SearchFilterViewModel)}
                                        variant={currentFilters[key] ? 'filled' : 'outlined'}
                                    />
                                </Grid>
                                <Grid size={(key === 'active' || key === 'administratorSitesOnly') ? 'auto' : 4}>
                                    {currentFilters[key] && renderFilterComponent(key, !currentFilters[key])}
                                </Grid>
                            </Fragment>
                        ))}
                </Grid>
                <DialogActions>
                    <Button type="button" onClick={handleSave} variant="contained">Search</Button>
                    <Button type="button" onClick={() => onClose(false)} color="error">Cancel</Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}

class DateRange {
    public start: Date | null = null;
    public end: Date | null = null;
}
